import type { GfxController } from '../core/GfxController';
import {
  labelFor,
  NOTE_COLORS,
  type GfxElement,
  type NoteProps,
  type PhotoProps,
  type RefKind,
  type TextProps,
} from '../core/types';

const REF_LABEL: Record<RefKind, string> = {
  target: '对象',
  entity: '线索',
  evidence: '证据',
  hypothesis: '研判',
};

/**
 * DOM 渲染层（借鉴 AFFiNE dom-renderer）：按视口裁剪懒挂载可编辑节点。
 * 节点使用卷宗索引卡样式，文本编辑走 contenteditable。
 */
export class DomRenderer {
  private nodes = new Map<string, HTMLElement>();
  private editingId: string | null = null;

  constructor(
    private host: HTMLElement,
    private controller: GfxController
  ) {}

  sync(): void {
    const { viewport, layer } = this.controller;
    const visible = viewport.overscanBounds(0.25);
    const alive = new Set<string>();

    for (const el of layer.domElements) {
      const inView =
        el.x + el.w >= visible.x &&
        el.x <= visible.x + visible.w &&
        el.y + el.h >= visible.y &&
        el.y <= visible.y + visible.h;
      if (!inView && this.editingId !== el.id) continue;

      alive.add(el.id);
      let node = this.nodes.get(el.id);
      if (!node || node.dataset.type !== el.type) {
        node?.remove();
        node = this.createNode(el);
        this.nodes.set(el.id, node);
        this.host.appendChild(node);
      }
      this.updateNode(node, el);
    }

    for (const [id, node] of [...this.nodes]) {
      if (alive.has(id)) continue;
      node.remove();
      this.nodes.delete(id);
      if (this.editingId === id) this.editingId = null;
    }
    this.host.style.pointerEvents = 'none';
  }

  private createNode(el: GfxElement): HTMLElement {
    const node = document.createElement('div');
    node.dataset.type = el.type;
    node.dataset.id = el.id;
    node.style.position = 'absolute';
    node.style.transformOrigin = '0 0';
    node.style.pointerEvents = 'auto';
    node.style.boxSizing = 'border-box';

    if (el.type === 'note') {
      node.innerHTML =
        '<div class="dossier-card">' +
        '<div class="dossier-rule"></div>' +
        '<div class="dossier-head"><span class="dossier-tag"></span><span class="dossier-title"></span></div>' +
        '<div class="dossier-body" contenteditable="false"></div>' +
        '</div>';
      const body = node.querySelector('.dossier-body') as HTMLElement;
      this.bindEditor(body, el.id, 'note');
    } else if (el.type === 'photo') {
      node.innerHTML =
        '<div class="photo-card">' +
        '<div class="photo-frame"><img class="photo-img" alt="" draggable="false" /></div>' +
        '<div class="photo-caption" contenteditable="false"></div>' +
        '</div>';
      const caption = node.querySelector('.photo-caption') as HTMLElement;
      this.bindEditor(caption, el.id, 'photo');
    } else {
      node.innerHTML = '<div class="dossier-text" contenteditable="false"></div>';
      const inner = node.querySelector('.dossier-text') as HTMLElement;
      this.bindEditor(inner, el.id, 'text');
    }
    return node;
  }

  private bindEditor(editable: HTMLElement, id: string, kind: 'note' | 'text' | 'photo'): void {
    editable.addEventListener('dblclick', () => this.startEditing(id));
    editable.addEventListener('blur', () => this.stopEditing(id));
    editable.addEventListener('input', () => {
      const current = this.controller.surface.get(id);
      if (!current) return;
      if (kind === 'note') {
        this.controller.surface.update(id, {
          props: { ...(current.props as NoteProps), text: editable.innerText },
        });
      } else if (kind === 'photo') {
        this.controller.surface.update(id, {
          props: { ...(current.props as PhotoProps), caption: editable.innerText },
        });
      } else {
        this.controller.surface.update(id, {
          props: { ...(current.props as TextProps), text: editable.innerText },
        });
      }
    });
    editable.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        editable.blur();
      }
      event.stopPropagation();
    });
  }

  private updateNode(node: HTMLElement, el: GfxElement): void {
    const { viewport, selection } = this.controller;
    const p = viewport.modelToScreen([el.x, el.y]);
    const zoom = viewport.zoom;
    node.style.width = `${el.w}px`;
    if (el.type === 'text') node.style.height = 'auto';
    else node.style.height = `${el.h}px`;
    node.style.transform = `translate(${p[0]}px, ${p[1]}px) scale(${zoom})`;
    node.style.zIndex = '10';

    const selected = selection.has(el.id);

    if (el.type === 'note') {
      const props = el.props as unknown as NoteProps;
      const colors = NOTE_COLORS[props.color] ?? NOTE_COLORS.default;
      const card = node.querySelector('.dossier-card') as HTMLElement;
      card.style.setProperty('--accent', colors.accent);
      card.classList.toggle('is-selected', selected);
      card.style.transform = el.rotate ? `rotate(${el.rotate}deg)` : '';

      const rule = node.querySelector('.dossier-rule') as HTMLElement;
      rule.style.background = colors.accent;
      const tag = node.querySelector('.dossier-tag') as HTMLElement;
      tag.style.background = colors.accent;

      const title = node.querySelector('.dossier-title') as HTMLElement;
      const ref = props.refKind ? REF_LABEL[props.refKind] : '';
      title.textContent = props.title ?? (ref ? `关联${ref}` : '情报卡片');

      const body = node.querySelector('.dossier-body') as HTMLElement;
      if (this.editingId !== el.id && body.innerText !== (props.text ?? '')) {
        body.innerText = props.text ?? '';
      }
      body.contentEditable = this.editingId === el.id ? 'true' : 'false';
      node.title = props.refKind && props.refId ? `${REF_LABEL[props.refKind]}：${props.refId}` : '';
    } else if (el.type === 'photo') {
      const props = el.props as unknown as PhotoProps;
      const card = node.querySelector('.photo-card') as HTMLElement;
      card.classList.toggle('is-selected', selected);
      card.classList.toggle('frame-plain', props.frame === 'plain');
      card.style.transform = el.rotate ? `rotate(${el.rotate}deg)` : '';
      const img = node.querySelector('.photo-img') as HTMLImageElement;
      if (img.getAttribute('src') !== props.src) img.setAttribute('src', props.src ?? '');
      const caption = node.querySelector('.photo-caption') as HTMLElement;
      if (this.editingId !== el.id && caption.innerText !== (props.caption ?? '')) {
        caption.innerText = props.caption ?? '';
      }
      caption.contentEditable = this.editingId === el.id ? 'true' : 'false';
      caption.style.display = props.frame === 'plain' && !props.caption ? 'none' : '';
    } else {
      const props = el.props as unknown as TextProps;
      const inner = node.querySelector('.dossier-text') as HTMLElement;
      inner.style.fontSize = `${props.fontSize ?? 20}px`;
      inner.style.fontWeight = props.bold ? '700' : '400';
      inner.style.color = props.color ?? '#1f2937';
      inner.style.outline = selected ? '1px dashed #1e3a5f' : 'none';
      inner.style.outlineOffset = '2px';
      if (this.editingId !== el.id && inner.innerText !== (props.text ?? '')) {
        inner.innerText = props.text ?? '';
      }
      inner.contentEditable = this.editingId === el.id ? 'true' : 'false';
    }
  }

  private startEditing(id: string): void {
    this.editingId = id;
    const node = this.nodes.get(id);
    if (!node) return;
    const editable = node.querySelector('.dossier-body, .dossier-text, .photo-caption') as HTMLElement;
    editable.contentEditable = 'true';
    editable.focus();
    const range = document.createRange();
    range.selectNodeContents(editable);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  private stopEditing(id: string): void {
    if (this.editingId !== id) return;
    this.editingId = null;
    const node = this.nodes.get(id);
    if (!node) return;
    const editable = node.querySelector('.dossier-body, .dossier-text, .photo-caption') as HTMLElement;
    editable.contentEditable = 'false';
    this.controller.notifyChange();
  }

  isEditing(id: string): boolean {
    return this.editingId === id;
  }

  destroy(): void {
    for (const node of this.nodes.values()) node.remove();
    this.nodes.clear();
  }
}

export { labelFor };